import { debugNoCodeInteractions } from '@unifyapps/carbon/consts/debug';
import { useGetDependencyGraphProvider } from '@unifyapps/carbon/no-code/context/DependencyGraphProvider/hooks/useDependencyGraphProvider';
import type { DependencyGraphContextType } from '@unifyapps/carbon/no-code/context/DependencyGraphProvider/types';
import type {
  ComputedJavaScriptVariableState,
  JavaScriptVariable,
  JavaScriptVariableState,
  SetVariableArrayTypePayload,
  SetVariableBooleanTypePayload,
  SetVariableNumberTypePayload,
  SetVariableObjectTypePayload,
  SetVariablePayloadType,
  SetVariableStringTypePayload,
} from '@unifyapps/defs/types/javascriptVariable';
import { JavaScriptVariableType } from '@unifyapps/defs/types/javascriptVariable';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { freeze } from 'immer';
import { useCallback } from 'react';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import { useAppDependencyGraphProvider } from '../../../../context/DependencyGraphProvider';
import InterfacePageHelper from '../../../../helper/InterfacePage';
import {
  useGetGlobalAppStateStoreState,
  useGlobalAppStateStore,
} from '../../../../stores/app/GlobalAppStateStore';
import { useGetGlobalStateStoreStateByInstanceId } from '../../../../stores/GlobalStateStore';
import {
  useGetActivePageByInstanceId,
  useInterfaceAppStore,
  useInterfaceStore,
} from '../../../../stores/InterfaceStore';
import { ComputeContextKeys } from '../../../computeContext/const';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { useReportActionError } from '../../../useReportActionError';
import { SHOULD_NOT_PROPAGATE_UP, SHOULD_PROPAGATE_UP } from '../../const';
import {
  getNextPageVariableStateForArray,
  getNextPageVariableStateForBoolean,
  getNextPageVariableStateForNumber,
  getNextPageVariableStateForObject,
  getNextPageVariableStateForString,
} from '../../utils/getNextState';

type PageVariableDependencies = {
  getPageVariableDetails: (
    variableId: string,
    instanceId?: string,
  ) => JavaScriptVariable | undefined;
  getPageVariableState: (
    variableId: string,
    instanceId?: string,
  ) => JavaScriptVariableState | undefined;
  getDependencyGraphProvider: (instanceId?: string) => DependencyGraphContextType;
  setPageVariableState: (
    variableId: string,
    state: JavaScriptVariableState | undefined,
    instanceId?: string,
  ) => void;
};

const useSetPageVariableBase = (deps: PageVariableDependencies) => {
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const reportActionError = useReportActionError();

  const onSetPageVariable: ActionHandlerType = useEventCallback(
    ({ action, actionContext, instanceId, blockId }: OnActionArgs) => {
      const payload = action.payload as unknown as SetVariablePayloadType;

      const pageVariableDetails = deps.getPageVariableDetails(payload.variableId, instanceId);

      if (!pageVariableDetails) {
        debugNoCodeInteractions('useSetPageVariableBase', 'pageVariableDetails not found', {
          payload,
        });
        return Promise.resolve(SHOULD_PROPAGATE_UP);
      }

      // allow initialization from undefined state
      const variableState = deps.getPageVariableState(payload.variableId, instanceId) ?? {
        id: payload.variableId,
        name: pageVariableDetails.name,
        value: undefined,
      };

      const { value: operationValue } = getNoCodeComputedData<{
        value: unknown;
      }>(
        {
          value: payload.operationDetails.value,
        },
        actionContext,
      );

      const { value: currentVariableState, index: operationIndex } = getNoCodeComputedData<{
        value: unknown;
        index?: number;
      }>(
        {
          value: variableState.value,
          index: 'index' in payload.operationDetails ? payload.operationDetails.index : undefined,
        },
        actionContext,
      );

      // when the payload is invalid, each type handler calls this — mirror the rejection to Pulse
      // as an error carrying the attempted payload (the shared reporter handles scope + guards).
      const onError = (message: string) => {
        reportActionError({
          source: 'action',
          error: message,
          instanceId,
          blockId,
          // the attempted set, as a structured object — rendered in the error's Payload view.
          payload: {
            variableId: payload.variableId,
            operation: payload.operationDetails.operation,
            value: operationValue,
          },
        });
      };

      let nextPageVariableState: ComputedJavaScriptVariableState<unknown> | undefined;

      switch (pageVariableDetails.type) {
        case JavaScriptVariableType.String:
          nextPageVariableState = getNextPageVariableStateForString({
            variableState: {
              ...variableState,
              value: currentVariableState,
            },
            operationDetails: {
              ...(payload.operationDetails as SetVariableStringTypePayload['operationDetails']),
              value: operationValue,
            },
            onError,
          });
          break;
        case JavaScriptVariableType.Number:
          nextPageVariableState = getNextPageVariableStateForNumber({
            variableState: {
              ...variableState,
              value: currentVariableState,
            },
            operationDetails: {
              ...(payload.operationDetails as SetVariableNumberTypePayload['operationDetails']),
              value: operationValue,
            },
            onError,
          });
          break;
        case JavaScriptVariableType.Boolean:
          nextPageVariableState = getNextPageVariableStateForBoolean({
            variableState: {
              ...variableState,
              value: currentVariableState,
            },
            operationDetails: {
              ...(payload.operationDetails as SetVariableBooleanTypePayload['operationDetails']),
              value: operationValue,
            },
          });
          break;
        case JavaScriptVariableType.Object:
          nextPageVariableState = getNextPageVariableStateForObject({
            variableState: {
              ...variableState,
              value: currentVariableState,
            },
            operationDetails: {
              ...(payload.operationDetails as SetVariableObjectTypePayload['operationDetails']),
              value: operationValue,
            },
            onError,
          });
          break;
        case JavaScriptVariableType.Array:
          nextPageVariableState = getNextPageVariableStateForArray({
            variableState: {
              ...variableState,
              value: currentVariableState,
            },
            operationDetails: {
              ...(payload.operationDetails as SetVariableArrayTypePayload['operationDetails']),
              value: operationValue,
              index: operationIndex,
            },
            onError,
          });
          break;
      }

      const frozenPageVariable = freeze(nextPageVariableState, true);
      deps.setPageVariableState(payload.variableId, frozenPageVariable, instanceId);
      const {
        actions: { notifyDependencyChange },
      } = deps.getDependencyGraphProvider(instanceId);
      notifyDependencyChange({ id: payload.variableId });
      notifyDependencyChange({ id: ComputeContextKeys.PAGE_VARIABLES });

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
  );

  return { onSetPageVariable };
};

export function useSetPageVariable() {
  const getGlobalStateStoreState = useGetGlobalStateStoreStateByInstanceId();
  const getActivePageByInstanceId = useGetActivePageByInstanceId();
  const pageVariables = useInterfaceStore().hooks.page.usePageVariables();
  const { getDependencyGraphProvider } = useGetDependencyGraphProvider();

  const getPageVariableDetails = useCallback(
    (variableId: string, instanceId?: string) => {
      if (instanceId) {
        const page = getActivePageByInstanceId(instanceId);
        if (page) {
          return InterfacePageHelper.getPageVariableById({
            interfacePage: page,
            id: variableId,
          });
        }
      }
      return pageVariables?.[variableId];
    },
    [getActivePageByInstanceId, pageVariables],
  );

  const getPageVariableState = useCallback(
    (variableId: string, instanceId?: string) => {
      return getGlobalStateStoreState(instanceId).pageVariables[variableId];
    },
    [getGlobalStateStoreState],
  );

  const setPageVariableStateInContext = useCallback(
    (variableId: string, state: JavaScriptVariableState | undefined, instanceId?: string) => {
      getGlobalStateStoreState(instanceId).actions.setPageVariableState(variableId, state);
    },
    [getGlobalStateStoreState],
  );

  return useSetPageVariableBase({
    getPageVariableDetails,
    getPageVariableState,
    setPageVariableState: setPageVariableStateInContext,
    getDependencyGraphProvider,
  });
}

export const useSetGlobalPageVariable = () => {
  const globalAppStateStoreActions = useGlobalAppStateStore().hooks.useActions();
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const pageVariables = useInterfaceAppStore().hooks.page.usePageVariables();
  const appDependencyGraphProvider = useAppDependencyGraphProvider();

  const getPageVariableDetails = useCallback(
    (variableId: string, _instanceId?: string) => {
      return pageVariables?.[variableId];
    },
    [pageVariables],
  );

  const getPageVariableState = useCallback(
    (variableId: string, _instanceId?: string) => {
      return getGlobalAppStateStoreState().pageVariables[variableId];
    },
    [getGlobalAppStateStoreState],
  );

  const setPageVariableState = useCallback(
    (variableId: string, state: JavaScriptVariableState | undefined, _instanceId?: string) => {
      globalAppStateStoreActions.setPageVariableState(variableId, state);
    },
    [globalAppStateStoreActions],
  );

  const getDependencyGraphProvider = useCallback(() => {
    return appDependencyGraphProvider;
  }, [appDependencyGraphProvider]);

  return useSetPageVariableBase({
    getPageVariableDetails,
    getPageVariableState,
    setPageVariableState,
    getDependencyGraphProvider,
  });
};
